// Runs the match score in the browser: decodes the recording and the model clips to 16 kHz mono
// and compares them. Everything stays on the device.

import { match, SAMPLE_RATE, type MatchResult } from './match';

type OfflineCtor = typeof OfflineAudioContext;

const Offline = (): OfflineCtor =>
  window.OfflineAudioContext ?? (window as unknown as { webkitOfflineAudioContext: OfflineCtor }).webkitOfflineAudioContext;

/**
 * Resamples with a windowed-sinc low-pass first, so sound above the new Nyquist frequency doesn't
 * fold back into the speech band (that would make every recording look alike). Used only where
 * the browser can't resample for us.
 */
export function resample(input: Float32Array, from: number, to: number): Float32Array {
  if (from === to) return input;
  const ratio = from / to;
  const cutoff = (0.45 * to) / from; // just under the new Nyquist, as a fraction of the input rate
  const half = 16;
  const taps = Array.from({ length: 2 * half + 1 }, (_, k) => {
    const n = k - half;
    const sinc = n === 0 ? 2 * cutoff : Math.sin(2 * Math.PI * cutoff * n) / (Math.PI * n);
    const window = 0.42 + 0.5 * Math.cos((Math.PI * n) / half) + 0.08 * Math.cos((2 * Math.PI * n) / half); // Blackman
    return sinc * window;
  });
  const out = new Float32Array(Math.floor(input.length / ratio));
  for (let i = 0; i < out.length; i++) {
    const center = Math.round(i * ratio);
    let sum = 0;
    for (let k = -half; k <= half; k++) {
      const j = center + k;
      if (j >= 0 && j < input.length) sum += input[j] * taps[k + half];
    }
    out[i] = sum;
  }
  return out;
}

async function decode(buffer: ArrayBuffer): Promise<Float32Array> {
  const Ctor = Offline();
  // Decode at a rate every browser accepts, then let the browser resample to 16 kHz properly.
  const audio = await new Ctor(1, 1, 44_100).decodeAudioData(buffer);
  try {
    const ctx = new Ctor(1, Math.max(1, Math.ceil(audio.duration * SAMPLE_RATE)), SAMPLE_RATE);
    const source = ctx.createBufferSource();
    source.buffer = audio;
    source.connect(ctx.destination);
    source.start();
    const rendered = await ctx.startRendering();
    return Float32Array.from(rendered.getChannelData(0));
  } catch {
    const mono = new Float32Array(audio.length);
    for (let c = 0; c < audio.numberOfChannels; c++) {
      const data = audio.getChannelData(c);
      for (let i = 0; i < data.length; i++) mono[i] += data[i] / audio.numberOfChannels;
    }
    return resample(mono, audio.sampleRate, SAMPLE_RATE);
  }
}

const models = new Map<string, Promise<Float32Array>>();

function model(src: string): Promise<Float32Array> {
  let p = models.get(src);
  if (!p) {
    p = fetch(src)
      .then((r) => r.arrayBuffer())
      .then(decode);
    p.catch(() => models.delete(src));
    models.set(src, p);
  }
  return p;
}

/** Compares a recording with the model clips and returns the best match. */
export async function matchRecording(recording: Blob, modelSrcs: string[]): Promise<MatchResult> {
  const [attempt, ...refs] = await Promise.all([recording.arrayBuffer().then(decode), ...modelSrcs.map(model)]);
  return match(attempt, refs);
}
